import { createAsyncThunk } from "@reduxjs/toolkit";

import { authSliceReducer } from "./authentication/store/auth.slice";
import { AUTH_MODULE_NAME } from "./authentication/store/constants";
import { appUsersApiSlice } from "./modules/app-users/store/app-users.rtkq";
import { appUsersReducer } from "./modules/app-users/store/app-users.slice";
import { APP_USERS_MODULE_NAME } from "./modules/app-users/store/constants";
import { cropsReducer } from "./modules/crop";
import { CROPS_MODULE_NAME } from "./modules/crop/store/constants";
import { employeesReducer } from "./modules/employees/store";
import { EMPLOYEES_MODULE_NAME } from "./modules/employees/store/constants";
import { ERRORS_MODULE_NAME } from "./modules/errors/store/constants";
import { errorsReducer } from "./modules/errors/store/errors.slice";
import { farmsApi } from "./modules/farms/store/farms.rtkq";
import { FILTERS_MODULE_NAME } from "./modules/filter/store/constants";
import { fetchFiltersAction, filtersReducer } from "./modules/filter/store/filters.slice";
import { INFO_DATA_KEY, infoDataReducer } from "./modules/info-data";
import { setInfoData } from "./modules/info-data/info-data-actions";
import { departmentsApi } from "./modules/info-data/store/departments.rtkq";
import { measuresApi } from "./modules/info-data/store/measures.rtkq";
import { positionsApi } from "./modules/info-data/store/positions.rtkq";
import { seasonsApi } from "./modules/info-data/store/seasons.rtkq";
import { techOperationGroupsApi } from "./modules/info-data/store/tech-operation-groups.rtkq";
import { techOperationScalarsApi } from "./modules/info-data/store/tech-operation-scalars.rtkq";
import { techOperationSubGroupsApi } from "./modules/info-data/store/tech-operation-subgroups.rtkq";
import { techOperationMeasuresSetsApi } from "./modules/info-data/store/tech-operations-measures-sets.rtkq";
import { techOperationParamsApi } from "./modules/info-data/store/tech-operations-params.rtkq";
import { workPlacesApi } from "./modules/info-data/store/workplaces.rtkq";
import { NotificationSeverity } from "./modules/notifications/shared/enums/notification-severity";
import { NotificationType } from "./modules/notifications/shared/enums/notification-type";
import { NOTIFICATIONS_MODULE_NAME } from "./modules/notifications/store/constants";
import {
  addNotificationAction,
  notificationsReducer,
} from "./modules/notifications/store/notifications.slice";
import { PHENOPHASES_MODULE_NAME } from "./modules/phenophases/store/constants";
import { phenophasesApi } from "./modules/phenophases/store/phenophases.rtkq";
import { phenophasesUiReducer } from "./modules/phenophases/store/phenophases.slice";
import { seedsReducer } from "./modules/seeds/store";
import { SEEDS_MODULE_NAME } from "./modules/seeds/store/constants";
import { techAssetsReducer } from "./modules/tech-assets/store";
import { TECH_ASSETS_MODULE_NAME } from "./modules/tech-assets/store/constants";
import { VERIFICATION_MODULE_NAME, verificationReducer } from "./modules/verification";
import { assetStocksPageReducer } from "./pages/asset-stocks/store/asset-stocks.slice";
import { ASSET_STOCKS_PAGE_MODULE_NAME } from "./pages/asset-stocks/store/constants";
import { assetUsageRecordsReducer } from "./pages/asset-usage-records/store/asset-usage-records.slice";
import { ASSET_USAGE_RECORDS_MODULE_NAME } from "./pages/asset-usage-records/store/constants";
import {
  FARM_LANDS_TECH_MODULE_NAME,
  FARM_LANDS_WEATHER_MODULE_NAME,
} from "./pages/fields/store/constants";
import {
  CROP_ROTATION_MODULE_NAME,
  cropRotationReducer,
} from "./pages/fields/store/crop-rotation.slice";
import { farmLandsTechReducer } from "./pages/fields/store/farm-lands-tech.slice";
import { farmLandsWeatherReducer } from "./pages/fields/store/farm-lands-weather.slice";
import {
  FIELDS_MODULE_NAME,
  farmLandsReducer,
} from "./pages/fields/store/farm-lands.slice";
import { mapControlsReducer, mapReducer } from "./pages/map-page";
import {
  MAP_CONTROLS_MODULE_NAME,
  MAP_PAGE_MODULE_NAME,
} from "./pages/map-page/store/constants";
import { OBSERVATION_EDITING_MODULE_NAME } from "./pages/observations/store/constants";
import { observationEditingReducer } from "./pages/observations/store/observation-editing.slice";
import {
  OBSERVATIONS_MODULE_NAME,
  observationsReducer,
} from "./pages/observations/store/observations.slice";
import {
  TECHOPERATIONS_MODULE_NAME,
  techOperationsReducer,
} from "./pages/tech-operations";
import { TECH_WORKS_MODULE_NAME } from "./pages/tech-works/store/constants";
import { techWorksReducer } from "./pages/tech-works/store/tech-works.slice";

export const dynamicReducers = {
  [AUTH_MODULE_NAME]: authSliceReducer,
  // TEMPORARY: DEVELOP-6043 - удалить вместе с модулем app-users при переходе на IMS
  [APP_USERS_MODULE_NAME]: appUsersReducer,
  [INFO_DATA_KEY]: infoDataReducer,
  [CROP_ROTATION_MODULE_NAME]: cropRotationReducer,
  [EMPLOYEES_MODULE_NAME]: employeesReducer,
  [FIELDS_MODULE_NAME]: farmLandsReducer,
  [FARM_LANDS_TECH_MODULE_NAME]: farmLandsTechReducer,
  [FARM_LANDS_WEATHER_MODULE_NAME]: farmLandsWeatherReducer,
  [MAP_CONTROLS_MODULE_NAME]: mapControlsReducer,
  [MAP_PAGE_MODULE_NAME]: mapReducer,
  [OBSERVATIONS_MODULE_NAME]: observationsReducer,
  [OBSERVATION_EDITING_MODULE_NAME]: observationEditingReducer,
  [SEEDS_MODULE_NAME]: seedsReducer,
  [TECH_ASSETS_MODULE_NAME]: techAssetsReducer,
  [TECHOPERATIONS_MODULE_NAME]: techOperationsReducer,
  [CROPS_MODULE_NAME]: cropsReducer,
  [FILTERS_MODULE_NAME]: filtersReducer,
  [ERRORS_MODULE_NAME]: errorsReducer,
  [NOTIFICATIONS_MODULE_NAME]: notificationsReducer,
  [ASSET_USAGE_RECORDS_MODULE_NAME]: assetUsageRecordsReducer,
  [ASSET_STOCKS_PAGE_MODULE_NAME]: assetStocksPageReducer,
  [VERIFICATION_MODULE_NAME]: verificationReducer,
  [PHENOPHASES_MODULE_NAME]: phenophasesUiReducer,
  [TECH_WORKS_MODULE_NAME]: techWorksReducer,
} as const;

export const loadDictionariesAction = createAsyncThunk<Promise<void>, void>(
  `GLOBAL/loadDictionaries`,
  async (_, { dispatch }) => {
    try {
      // ASYNCHRONOUS_LOADING
      Promise.all([
        // moved here from SYNCHRONOUS_LOADING (4026, accelerated first page load)
        dispatch(fetchFiltersAction()).unwrap(),

        // don't make user wait, 10Mb will land on page later using getTechAssetsLoading
        // MOVED_TO_techop-assets.rtkq.ts dispatch(fetchTechAssetsAction()),
        // MOVED_TO_asset-types.rtkq.ts   dispatch(fetchAssetTypesAction()),
        // MOVED_TO_seeds.rtkq.ts         dispatch(fetchAllSeedsIncludeDeletedAction()),
      ]);

      // SYNCHRONOUS_LOADING `Загружаются справочники...` in protected-route.tsx
      await Promise.all([
        dispatch(appUsersApiSlice.endpoints.fetchAppUsers.initiate()).unwrap(),
        dispatch(
          departmentsApi.endpoints.fetchDepartmentsWithDeleted.initiate()
        ).unwrap(),
        dispatch(farmsApi.endpoints.fetchFarms.initiate()).unwrap(),
        dispatch(positionsApi.endpoints.fetchPositions.initiate()).unwrap(),
        dispatch(workPlacesApi.endpoints.fetchWorkPlaces.initiate()).unwrap(),
        dispatch(seasonsApi.endpoints.fetchSeasons.initiate()).unwrap(),
        dispatch(phenophasesApi.endpoints.fetchPhenophases.initiate()).unwrap(),
        dispatch(
          techOperationGroupsApi.endpoints.fetchTechOperationGroupsWithDeleted.initiate()
        ).unwrap(),
        dispatch(
          techOperationSubGroupsApi.endpoints.fetchTechOperationSubGroupsWithDeleted.initiate()
        ).unwrap(),
        dispatch(
          techOperationScalarsApi.endpoints.fetchTechOperationScalars.initiate()
        ).unwrap(),
        dispatch(
          techOperationMeasuresSetsApi.endpoints.fetchTechOperationMeasuresSetsWithDeleted.initiate()
        ).unwrap(),
        dispatch(
          techOperationParamsApi.endpoints.fetchTechOperationParamsWithDeleted.initiate()
        ).unwrap(),
        dispatch(measuresApi.endpoints.fetchMeasuresWithDeleted.initiate()).unwrap(),
        dispatch(
          setInfoData({
            techAssetCategory: ["Самоходная", "Прицепная"],
          })
        ),
      ]);
      dispatch(
        setInfoData({
          initialDictionariesLoaded: true,
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(
        addNotificationAction({
          type: NotificationType.Snack,
          severity: NotificationSeverity.Error,
          error: e,
          message: "При загрузке словарей возникла ошибка",
        })
      );
    }
  }
);
